import {
  useForecastTemplateLimits,
  useInputFieldLimits,
  useInputFieldRules,
  useLocale,
  usePowerLevels,
  usePowerLevelsRules,
} from '@e2m-customer-portal/hooks';
import { convertMeasure, InputFieldType } from '@e2m-customer-portal/shared';
import { Dialog } from '@e2m-customer-portal/ui';
import { Button, DialogContent } from '@mui/material';
import clsx from 'clsx';
import { addMinutes, endOfDay, Interval, isAfter, isBefore, isWeekend } from 'date-fns';
import {
  Device,
  EditableMarket,
  ForecastProperty,
  ForecastSlice,
  ForecastTarget,
  MarketLocation,
  Maybe,
  Resolution,
} from 'generated/graphql';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SpotPriceResult } from '../../../spot-price';
import { switchFocusForDeviceMap } from '../../../utils';
import { DecoratedCell } from '../DecoratedCell';

interface DSTAdjustmentModalProps {
  open: boolean;
  toggleModal: () => void;
  marketLocation: MarketLocation;
  device: Device;
  interval: Interval;
  deadlineDate?: Date;
  slices: readonly ForecastSlice[];
  dstSliceValues?: Map<string, Array<Array<{ slice: ForecastSlice; value: string }>>>;
  setDSTSliceValues?: React.Dispatch<
    React.SetStateAction<Map<string, Array<Array<{ slice: ForecastSlice; value: string }>>>>
  >;
  showSpotPrices?: boolean;
  spotPrice: (sliceDate: Date) => SpotPriceResult | undefined;
  latestHistoricalSpotPrice?: Maybe<Date>;
  readOnly?: boolean;
  resetTrigger: boolean;
  editedSlices: Set<string>;
  setEditedSlices: React.Dispatch<React.SetStateAction<Set<string>>>;
  setRevokeDowntimeInterval: React.Dispatch<React.SetStateAction<Interval | null>>;
  currentForecastProperty?: ForecastProperty;
}

export function DSTAdjustmentModal({
  open,
  toggleModal,
  marketLocation,
  device,
  interval,
  deadlineDate,
  slices,
  dstSliceValues,
  setDSTSliceValues,
  showSpotPrices,
  spotPrice,
  latestHistoricalSpotPrice,
  readOnly,
  resetTrigger,
  editedSlices,
  setEditedSlices,
  setRevokeDowntimeInterval,
  currentForecastProperty,
}: Readonly<DSTAdjustmentModalProps>): ReactElement<DSTAdjustmentModalProps> {
  const [t] = useTranslation();
  const { code: locale } = useLocale();

  const limits = useInputFieldLimits(marketLocation.id, device.id, interval);
  const { maximum, minimum } = useForecastTemplateLimits(device.id, interval);
  const powerLevels = usePowerLevels(device.id) ?? [];
  const powerLevelRules = usePowerLevelsRules(powerLevels, 'kW');
  const rules = useInputFieldRules(slices[0].fields, limits, minimum, maximum)[InputFieldType.PRODUCTION];
  const isIntradayEditable =
    currentForecastProperty?.target === ForecastTarget.VPP &&
    currentForecastProperty?.editableMarkets.includes(EditableMarket.INTRADAY);
  const isFifteenMinuteResolution = currentForecastProperty?.resolution === Resolution.FIFTEEN_MINUTES;

  const isAfterDayAheadDeadline = deadlineDate && isBefore(endOfDay(slices[0].date), deadlineDate);
  const isAfterIntradayDeadline = isBefore(
    slices[0].date,
    addMinutes(
      new Date(),
      device.deadlines.find((deadline) => deadline.type === InputFieldType.PRODUCTION)?.deadline
        ?.minutesBeforeIntradayDelivery ?? 0,
    ),
  );
  const isAfterDeadline =
    (isIntradayEditable && isAfterIntradayDeadline) || (!isIntradayEditable && isAfterDayAheadDeadline);

  const afterLastIntradayDeadline = isAfter(
    new Date(),
    addMinutes(
      slices[7].date,
      device.deadlines.find((deadline) => deadline.type === InputFieldType.PRODUCTION)?.deadline
        ?.minutesBeforeIntradayDelivery ?? 0,
    ),
  );

  const fillOverviewMap = () =>
    new Map<string, Array<Array<HTMLInputElement | null>>>([
      [
        device.id,
        isFifteenMinuteResolution
          ? [
              [null, null],
              [null, null],
              [null, null],
              [null, null],
            ]
          : [[null, null]],
      ],
    ]);

  const [overviewMap, setOverviewMap] = useState(fillOverviewMap());
  const [dstEdits, setDSTEdits] = useState(
    new Set(slices.map((slice) => slice.id).filter((slice) => editedSlices.has(slice))),
  );
  const [dstErrors, setDSTErrors] = useState(new Set<string>());

  if (!slices.length || !setDSTSliceValues || !dstSliceValues) return <></>;

  const saveDSTChanges = () => {
    setDSTSliceValues((dstSliceValues) => {
      const sliceValues = new Map(dstSliceValues);
      const values = sliceValues.get(device.id);
      const deviceMap = overviewMap.get(device.id);
      if (!values || !deviceMap) return sliceValues;

      for (const [rowIndex, row] of deviceMap.entries()) {
        for (const [columnIndex, column] of row.entries()) {
          values[rowIndex][columnIndex].value =
            column?.value ??
            convertMeasure(
              slices[rowIndex * 2 + columnIndex].fields.find((value) => value.type === InputFieldType.PRODUCTION)
                ?.value ?? {
                value: null,
                unit: 'kW',
              },
              'kW',
            ).value?.toLocaleString(locale, { useGrouping: false }) ??
            '';
        }
      }

      return sliceValues.set(device.id, values);
    });
    setEditedSlices((editedSlices) => {
      const edits = new Set(editedSlices);
      for (const slice of slices) {
        edits.delete(slice.id);
      }
      for (const edit of dstEdits) {
        edits.add(edit);
      }
      return edits;
    });
    toggleModal();
  };

  useEffect(() => setOverviewMap(fillOverviewMap()), [slices.length]);

  const deviceMap = overviewMap.get(device.id);
  if (!deviceMap) throw new Error(`Data Error: Table overview for device with id "${device.id}" not found`);
  const switchFocus = useCallback(switchFocusForDeviceMap(deviceMap), [deviceMap]);

  return (
    <Dialog
      open={open}
      title={t('DAYLIGHT_SAVING_TIME_TITLE')}
      onClose={toggleModal}
      actions={
        <>
          <Button variant="outlined" onClick={toggleModal}>
            {t('SHARED_DISCARD')}
          </Button>
          <Button disabled={!!dstErrors.size || afterLastIntradayDeadline} onClick={saveDSTChanges}>
            {t('SHARED_SUBMIT')}
          </Button>
        </>
      }
    >
      <DialogContent className="flex flex-col gap-4">
        <div>
          {t('DAYLIGHT_SAVING_TIME_DESCRIPTION', {
            date: slices[0].date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' }),
          })}
        </div>
        <table className="border-spacing-0 bg-white" data-testid="DST-Adjustment-Table">
          <thead>
            <tr>
              <th className="border border-r-0 border-solid border-divider" />
              <th
                className={clsx('border border-r-0 border-solid border-divider py-2 text-sm font-normal leading-4', {
                  'bg-blue-grey-200/5': isWeekend(slices[0].date),
                  'text-black/60': isAfterDeadline,
                })}
              >
                {/* Trimming, because different browsers produce different substrings, some with a leading space, some without. */}
                {slices[0].date
                  .toLocaleTimeString(locale, { second: '2-digit', timeZoneName: 'long' })
                  .substring(2)
                  .trim()}
              </th>
              <th
                className={clsx('border border-solid border-divider py-2 text-sm font-normal leading-4', {
                  'bg-blue-grey-200/5': isWeekend(slices[1].date),
                  'text-black/60': isAfterDeadline,
                })}
              >
                {slices[1].date
                  .toLocaleTimeString(locale, { second: '2-digit', timeZoneName: 'long' })
                  .substring(2)
                  .trim()}
              </th>
            </tr>
          </thead>
          <tbody>
            {slices
              .reduce((sortedSlices, slice, index) => {
                if (index % 2) sortedSlices[sortedSlices.length - 1].push(slice);
                else sortedSlices.push([slice]);
                return sortedSlices;
              }, new Array<Array<ForecastSlice>>())
              .map((rowSlices, row) => (
                <tr key={rowSlices[1].id} className="even:bg-grey-50">
                  <th className="min-w-24 border-0 border-b border-l border-solid pl-2 text-start text-sm font-normal leading-4 border-grey-100">
                    {`${rowSlices[1].date.toLocaleTimeString(locale, {
                      hour: '2-digit',
                      minute: '2-digit',
                    })} - ${addMinutes(rowSlices[1].date, isFifteenMinuteResolution ? 15 : 60).toLocaleTimeString(
                      locale,
                      {
                        hour: '2-digit',
                        minute: '2-digit',
                        hourCycle: rowSlices[1].date.getHours() < 12 ? 'h23' : 'h24',
                      },
                    )}`}
                  </th>
                  {rowSlices.map((slice, column) => (
                    <DecoratedCell
                      key={slice.id}
                      device={device}
                      deadlineDate={deadlineDate}
                      slice={slice}
                      powerLevelRules={powerLevelRules}
                      rules={rules}
                      spotPrice={showSpotPrices ? spotPrice(slice.date) : undefined}
                      latestHistoricalSpotPrice={latestHistoricalSpotPrice}
                      cellIndex={{ row, column }}
                      readOnly={readOnly}
                      resetTrigger={resetTrigger}
                      editedSlices={dstEdits}
                      overviewMap={overviewMap}
                      setEditedSlices={setDSTEdits}
                      setErroneousSlices={setDSTErrors}
                      setRevokeDowntimeInterval={setRevokeDowntimeInterval}
                      setOverviewMap={setOverviewMap}
                      switchFocus={switchFocus}
                      saveChanges={saveDSTChanges}
                      currentForecastProperty={currentForecastProperty}
                      valueOverride={dstSliceValues.get(device.id)?.at(row)?.at(column)?.value}
                      className={column ? 'border-r border-grey-100' : 'border-grey-100'}
                    />
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </DialogContent>
    </Dialog>
  );
}
